import React, { createContext, useContext, useEffect, useState } from "react";

export type ITransaction = {
  id: string;
  description: string;
  amount: number;
  type: "income" | "expense" | "saving";
  date: string;
  category: string;
  transactionListDate: string;
  savingGoalId?: string;
};

type TransactionsContextType = {
  transactions: ITransaction[];
  addTransaction: (tx: ITransaction) => void;
  removeTransaction: (id: string) => void;
};

const TransactionsContext = createContext<TransactionsContextType | undefined>(
  undefined
);

export const useTransactions = () => {
  const context = useContext(TransactionsContext);
  if (!context)
    throw new Error("useTransactions must be used within TransactionsProvider");
  return context;
};

export const TransactionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [transactions, setTransactions] = useState<ITransaction[]>(() => {
    // Initialisierung direkt aus localStorage
    const saved = localStorage.getItem("transactions");
    return saved ? JSON.parse(saved) : [];
  });

  // Automatisch speichern bei jeder Änderung
  useEffect(() => {
    localStorage.setItem("transactions", JSON.stringify(transactions));
  }, [transactions]);

  const addTransaction = (tx: ITransaction) => {
    setTransactions((prev) => [tx, ...prev]);
  };

  const removeTransaction = (id: string) => {
    setTransactions((prev) => prev.filter((tx) => tx.id !== id));
  };

  return (
    <TransactionsContext.Provider
      value={{ transactions, addTransaction, removeTransaction }}
    >
      {children}
    </TransactionsContext.Provider>
  );
};
